import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Autocomplete,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  FactCheck as FactCheckIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  OpenInNew as OpenInNewIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import RecordEditDialog from './RecordEditDialog';
import ExportExclusionDialog, { setExportExclusion } from './ExportExclusionDialog';
import ValidationRateLines, { RateLine } from './ValidationRateLines';
import { bandInputInvalid } from '../../utils/bandRate';

interface Option {
  id: string;
  name: string;
}

// See api/DTOs/ValidationDto.cs.
interface RuleSummary {
  key: string;
  name: string;
  description: string;
  issueCount: number;
  dismissedCount: number;
}

interface IssueRecord {
  id: string;
  date: string;
  operationTypeName: string;
  products: string;
  geneStackName?: string;
  farmName?: string;
  // Null when the record is on the farm itself; unmatchedFieldText is then
  // the field its import row named, which was never matched to a field.
  fieldName?: string;
  unmatchedFieldText?: string;
  fromImport: boolean;
  // Left out of the export; listed only with "Show dismissed".
  excludedFromExport: boolean;
  exportExclusionNote?: string;
  // What the rule found on this record, e.g. the flagged line's rate.
  note?: string;
  // Dismissed for this issue's rule on its own — only listed with "Show dismissed".
  dismissal?: Dismissal;
}

interface Dismissal {
  id: string;
  note?: string;
  dismissedBy?: string;
  dateCreated: string;
}

interface Issue {
  ruleKey: string;
  subjectKey: string;
  businessId: string;
  businessName: string;
  farmName?: string;
  fieldName?: string;
  unmatchedFieldText?: string;
  farmId?: string;
  fieldId?: string;
  timePeriodId?: string;
  timePeriodName?: string;
  message: string;
  records: IssueRecord[];
  // The flagged product lines, for a rule whose subject is the line.
  lines: Omit<RateLine, 'issueDismissed' | 'businessId' | 'businessName' | 'date' | 'farmName' | 'fieldName'>[];
  dismissal?: Dismissal;
  // Records dismissed one by one; message still counts them.
  dismissedRecordCount: number;
}

const RATE_RULE = 'rate-outlier';

const issueKey = (issue: Issue) => `${issue.ruleKey}|${issue.subjectKey}`;

// An issue on one farm or field opens in Field view (pages/FieldView.tsx),
// the same link as RecordEditDialog's: a field season issue has no records
// to open.
const fieldViewUrl = (issue: Issue) => {
  const query = new URLSearchParams({ businessId: issue.businessId });
  if (issue.farmId) query.set('farmId', issue.farmId);
  if (issue.fieldId) query.set('fieldId', issue.fieldId);
  if (issue.timePeriodId) query.set('timePeriodId', issue.timePeriodId);
  return `/field-view?${query}`;
};

// For cleaning consultant data imported for a business: each rule
// (api/Services/Validation) checks live data and lists what looks wrong —
// it never changes a record. Fix a record on its business page, or dismiss
// an issue that's been checked and is fine.
const DataValidationManagement: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [timePeriods, setTimePeriods] = useState<Option[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [timePeriodId, setTimePeriodId] = useState('');
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [ruleFilter, setRuleFilter] = useState<string | null>(null);
  // The rate rule's flagged lines, one row each, or its issues (one per
  // business + product), as every other rule shows.
  const [rateView, setRateView] = useState<'lines' | 'issues'>('lines');
  // The rate-outlier rule's threshold: how many spreads from a product's
  // typical rate a line may be (api/Services/Validation/RateOutlierRule.cs).
  // Loosen or tighten it as bad rates are fixed.
  const [rateOutlierSds, setRateOutlierSds] = useState(2);

  const [rules, setRules] = useState<RuleSummary[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // recordIds: dismiss just those of the issue's records, for its rule.
  const [dismissTarget, setDismissTarget] = useState<{ issue: Issue; recordIds: string[] | null } | null>(null);
  const [dismissNote, setDismissNote] = useState('');
  const [saving, setSaving] = useState(false);
  // records: all of the issue's, or just the ticked ones.
  const [deleteTarget, setDeleteTarget] = useState<{ issue: Issue; records: IssueRecord[] } | null>(null);
  // A record double-clicked in an issue, open in RecordEditDialog.
  const [editTarget, setEditTarget] = useState<{ recordId: string; businessId: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Records ticked in each issue (by issueKey), to dismiss or restore some
  // of an issue's records rather than the whole issue.
  const [selected, setSelected] = useState<{ [issueKey: string]: Set<string> }>({});
  // Ticked records to leave out of the export, in ExportExclusionDialog.
  const [excludeTarget, setExcludeTarget] = useState<string[] | null>(null);
  // Ticked records to set a date, operation, region, gene stack and/or band
  // on (POST api/records/bulk-update). Blank fields are left as they are.
  const [updateTarget, setUpdateTarget] = useState<{ issue: Issue; records: IssueRecord[] } | null>(null);
  const [updateDate, setUpdateDate] = useState('');
  const [updateOperationTypeId, setUpdateOperationTypeId] = useState('');
  const [updateRegion, setUpdateRegion] = useState<Option | null>(null);
  const [updateGeneStack, setUpdateGeneStack] = useState<Option | null>(null);
  const [updateBand, setUpdateBand] = useState('');
  const [updating, setUpdating] = useState(false);
  const [operationTypes, setOperationTypes] = useState<Option[]>([]);
  const [regions, setRegions] = useState<Option[]>([]);
  const [geneStacks, setGeneStacks] = useState<Option[]>([]);
  const lastTicked = useRef<{ key: string; index: number } | null>(null);

  useEffect(() => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    (async () => {
      const [businessRes, tpRes] = await Promise.all([
        fetch('/api/businesses/list', { headers: authHeaders() }),
        fetch('/api/time-periods', { headers: authHeaders() }),
      ]);
      if (businessRes.ok) setBusinesses((await businessRes.json()).map((b: any) => ({ id: b.id, name: b.name })));
      if (tpRes.ok) setTimePeriods((await tpRes.json()).map((t: any) => ({ id: t.id, name: t.name })));
    })();
  }, [auth.isSignedIn, auth.tokens?.accessToken, authHeaders]);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ includeDismissed: String(includeDismissed), rateOutlierSds: String(rateOutlierSds) });
      if (businessId) params.set('businessId', businessId);
      if (timePeriodId) params.set('timePeriodId', timePeriodId);
      const res = await fetch(`/api/validation?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to run validation');
      const data = await res.json();
      setRules(data.rules);
      setIssues(data.issues);
      setSelected({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [businessId, timePeriodId, includeDismissed, rateOutlierSds, authHeaders]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // Shift-click ticks (or unticks) every record from the last one clicked.
  const tickRecord = (issue: Issue, index: number, shift: boolean) => {
    const key = issueKey(issue);
    const last = lastTicked.current;
    const [from, to] = shift && last?.key === key ? [Math.min(last.index, index), Math.max(last.index, index)] : [index, index];
    setSelected((prev) => {
      const next = new Set(prev[key]);
      const on = !next.has(issue.records[index].id);
      for (let i = from; i <= to; i++) {
        if (on) next.add(issue.records[i].id); else next.delete(issue.records[i].id);
      }
      return { ...prev, [key]: next };
    });
    lastTicked.current = { key, index };
  };

  const selectRecords = (issue: Issue, ids: string[]) => setSelected((prev) => ({ ...prev, [issueKey(issue)]: new Set(ids) }));

  const handleDismiss = async () => {
    if (!dismissTarget) return;
    const { issue, recordIds } = dismissTarget;
    setSaving(true);
    try {
      const res = await fetch(recordIds ? '/api/validation/dismissals/records' : '/api/validation/dismissals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(
          recordIds
            ? { ruleKey: issue.ruleKey, recordIds, note: dismissNote }
            : { ruleKey: issue.ruleKey, subjectKey: issue.subjectKey, note: dismissNote }
        ),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to dismiss');
      }
      setDismissTarget(null);
      await fetchIssues();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  // Hard-deletes the issue's records (api/records/bulk-delete), which puts
  // their import rows back to unmatched so they can be approved again from
  // the import's review — e.g. after correcting a wrong Field Map alias.
  const handleDeleteRecords = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch('/api/records/bulk-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(deleteTarget.records.map((r) => r.id)),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete the records');
      }
      setDeleteTarget(null);
      await fetchIssues();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (issue: Issue) => {
    if (!issue.dismissal) return;
    try {
      const res = await fetch(`/api/validation/dismissals/${issue.dismissal.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to restore');
      await fetchIssues();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const openUpdate = (issue: Issue, records: IssueRecord[]) => {
    setUpdateTarget({ issue, records });
    setUpdateDate('');
    setUpdateOperationTypeId('');
    setUpdateRegion(null);
    setUpdateGeneStack(null);
    setUpdateBand('');
    if (operationTypes.length > 0 && regions.length > 0 && geneStacks.length > 0) return;
    (async () => {
      const [opRes, regionRes, geneStackRes] = await Promise.all([
        fetch('/api/operation-types', { headers: authHeaders() }),
        fetch('/api/regions', { headers: authHeaders() }),
        fetch('/api/gene-stacks', { headers: authHeaders() }),
      ]);
      if (opRes.ok) setOperationTypes((await opRes.json()).filter((t: any) => !t.archivedDate).map((t: any) => ({ id: t.id, name: t.name })));
      if (regionRes.ok) setRegions((await regionRes.json()).filter((r: any) => !r.archivedDate).map((r: any) => ({ id: r.id, name: r.name })));
      if (geneStackRes.ok) setGeneStacks((await geneStackRes.json()).map((g: any) => ({ id: g.id, name: g.name })));
    })();
  };

  const handleUpdateRecords = async () => {
    if (!updateTarget) return;
    setUpdating(true);
    try {
      const res = await fetch('/api/records/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          recordIds: updateTarget.records.map((r) => r.id),
          date: updateDate || null,
          operationTypeId: updateOperationTypeId || null,
          regionId: updateRegion?.id ?? null,
          geneStackId: updateGeneStack?.id ?? null,
          bandPercent: updateBand.trim() ? Number(updateBand) : null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update the records');
      }
      setUpdateTarget(null);
      await fetchIssues();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setUpdating(false);
    }
  };

  const handleRestoreRecords = async (issue: Issue, recordIds: string[]) => {
    try {
      const res = await fetch('/api/validation/dismissals/records/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ruleKey: issue.ruleKey, recordIds }),
      });
      if (!res.ok) throw new Error('Failed to restore');
      await fetchIssues();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleIncludeRecords = async (recordIds: string[]) => {
    try {
      await setExportExclusion(recordIds, false, null, authHeaders);
      await fetchIssues();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const ruleName = (key: string) => rules.find((r) => r.key === key)?.name ?? key;
  const shown = ruleFilter ? issues.filter((i) => i.ruleKey === ruleFilter) : issues;
  const rateLines = useMemo<RateLine[]>(
    () => issues.filter((i) => i.ruleKey === RATE_RULE).flatMap((issue) => {
      const records = new Map(issue.records.map((r) => [r.id, r]));
      return issue.lines.map((line) => {
        const record = records.get(line.recordId);
        return {
          ...line,
          issueDismissed: !!issue.dismissal,
          businessId: issue.businessId,
          businessName: issue.businessName,
          date: record?.date ?? '',
          farmName: record?.farmName,
          fieldName: record?.fieldName ?? record?.unmatchedFieldText,
        };
      });
    }),
    [issues]
  );

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <FactCheckIcon />
          Data Validation
        </Typography>
        <IconButton onClick={fetchIssues} title="Run again"><RefreshIcon /></IconButton>
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={businesses}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={businesses.find((b) => b.id === businessId) || null}
          onChange={(_, value) => setBusinessId(value?.id || null)}
          renderInput={(params) => <TextField {...params} label="Business (and its children)" placeholder="All businesses" />}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="validation-season-label">Season</InputLabel>
          <Select labelId="validation-season-label" label="Season" value={timePeriodId} onChange={(e) => setTimePeriodId(e.target.value)}>
            <MenuItem value=""><em>Any</em></MenuItem>
            {timePeriods.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Rate looks wrong: how far from a product's typical rate (across all businesses) a line may be, in standard deviations. Lines within 5× of it are never flagged.">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="validation-rate-sds-label">Rate outlier SDs</InputLabel>
            <Select labelId="validation-rate-sds-label" label="Rate outlier SDs" value={rateOutlierSds} onChange={(e) => setRateOutlierSds(Number(e.target.value))}>
              {[1.5, 2, 2.5, 3, 4, 5].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
          </FormControl>
        </Tooltip>
        <FormControlLabel
          control={<Switch size="small" checked={includeDismissed} onChange={(e) => setIncludeDismissed(e.target.checked)} />}
          label="Show dismissed"
        />
      </Box>

      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {rules.map((rule) => (
          <Tooltip key={rule.key} title={rule.description}>
            <Chip
              clickable
              color={ruleFilter === rule.key ? 'primary' : rule.issueCount > 0 ? 'warning' : 'default'}
              variant={ruleFilter === rule.key ? 'filled' : 'outlined'}
              label={`${rule.name}: ${rule.issueCount}${rule.dismissedCount ? ` (+${rule.dismissedCount} dismissed)` : ''}`}
              onClick={() => setRuleFilter((prev) => (prev === rule.key ? null : rule.key))}
            />
          </Tooltip>
        ))}
      </Box>

      {ruleFilter === RATE_RULE && (
        <ToggleButtonGroup
          exclusive
          size="small"
          sx={{ mb: 2 }}
          value={rateView}
          onChange={(_, view) => { if (view) setRateView(view); }}
        >
          <ToggleButton value="lines">Lines</ToggleButton>
          <ToggleButton value="issues">Issues</ToggleButton>
        </ToggleButtonGroup>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && issues.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
      ) : shown.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No issues found.</Typography>
        </Paper>
      ) : ruleFilter === RATE_RULE && rateView === 'lines' ? (
        <ValidationRateLines
          lines={rateLines}
          authHeaders={authHeaders}
          onChanged={fetchIssues}
          onEditRecord={(recordId, recordBusinessId) => setEditTarget({ recordId, businessId: recordBusinessId })}
          onError={setError}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8125rem' }, '& .MuiTableCell-head': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Actions</TableCell>
                <TableCell>Rule</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Farm / Field</TableCell>
                <TableCell>Season</TableCell>
                <TableCell>Issue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((issue) => {
                const key = issueKey(issue);
                const open = expanded.has(key);
                return (
                  <React.Fragment key={key}>
                    <TableRow hover sx={issue.dismissal ? { opacity: 0.6 } : undefined}>
                      <TableCell padding="checkbox">
                        {issue.records.length > 0 && (
                          <IconButton size="small" onClick={() => toggleExpanded(key)} title={open ? 'Hide records' : 'Show records'}>
                            {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {issue.farmId ? (
                          <Tooltip title="Open the field view: its field seasons and records">
                            <IconButton size="small" onClick={() => window.open(fieldViewUrl(issue), '_blank')}><OpenInNewIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Open the business (fix records under Map / Records)">
                            <IconButton size="small" onClick={() => navigate(`/businesses/${issue.businessId}`)}><OpenInNewIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {issue.dismissal ? (
                          <Button size="small" onClick={() => handleRestore(issue)}>Restore</Button>
                        ) : (
                          <Button size="small" onClick={() => { setDismissTarget({ issue, recordIds: null }); setDismissNote(''); }}>Dismiss</Button>
                        )}
                        {issue.records.length > 0 && (
                          <Tooltip title="Delete these records so their import rows can be approved again">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget({ issue, records: issue.records })}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120, maxWidth: 170 }}>{ruleName(issue.ruleKey)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{issue.businessName}</TableCell>
                      <TableCell>
                        {issue.farmName ?? '—'}
                        {issue.fieldName && (
                          <Typography variant="caption" color="text.secondary" display="block">{issue.fieldName}</Typography>
                        )}
                        {issue.unmatchedFieldText && (
                          <Tooltip title="These records are on the farm, not a field: the fields their import rows named were never matched to a field">
                            <Typography variant="caption" color="warning.main" display="block">
                              {issue.unmatchedFieldText} (not matched)
                            </Typography>
                          </Tooltip>
                        )}
                        {!issue.fieldName && !issue.unmatchedFieldText && (
                          <Typography variant="caption" color="text.secondary" display="block">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{issue.timePeriodName ?? '—'}</TableCell>
                      <TableCell sx={{ minWidth: 280 }}>
                        {issue.message}
                        {issue.dismissal && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Dismissed{issue.dismissal.dismissedBy ? ` by ${issue.dismissal.dismissedBy}` : ''} on {new Date(issue.dismissal.dateCreated).toLocaleDateString('en-AU')}
                            {issue.dismissal.note ? ` — ${issue.dismissal.note}` : ''}
                          </Typography>
                        )}
                        {issue.dismissedRecordCount > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {issue.dismissedRecordCount} of these record{issue.dismissedRecordCount === 1 ? '' : 's'} dismissed or excluded from the export
                            {includeDismissed ? '' : ' (turn on Show dismissed to list them)'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    {issue.records.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                          <Collapse in={open} unmountOnExit>
                            {(() => {
                              const ticked = issue.records.filter((r) => selected[key]?.has(r.id));
                              const toDismiss = ticked.filter((r) => !r.dismissal && !r.excludedFromExport).map((r) => r.id);
                              const toRestore = ticked.filter((r) => r.dismissal).map((r) => r.id);
                              const toExclude = ticked.filter((r) => !r.excludedFromExport).map((r) => r.id);
                              const toInclude = ticked.filter((r) => r.excludedFromExport).map((r) => r.id);
                              return (
                                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mt={1} minHeight={32}>
                                  <Typography variant="caption" color="text.secondary">
                                    {ticked.length > 0
                                      ? `${ticked.length} of ${issue.records.length} selected`
                                      : 'Tick records to update, dismiss or delete just those (shift-click ticks a run of them)'}
                                  </Typography>
                                  {ticked.length > 0 && (
                                    <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />} onClick={() => openUpdate(issue, ticked)}>
                                      Update {ticked.length} record{ticked.length === 1 ? '' : 's'}
                                    </Button>
                                  )}
                                  {toDismiss.length > 0 && (
                                    <Button size="small" variant="outlined" onClick={() => { setDismissTarget({ issue, recordIds: toDismiss }); setDismissNote(''); }}>
                                      Dismiss {toDismiss.length} record{toDismiss.length === 1 ? '' : 's'}
                                    </Button>
                                  )}
                                  {toExclude.length > 0 && (
                                    <Button size="small" variant="outlined" onClick={() => setExcludeTarget(toExclude)}>
                                      Exclude {toExclude.length} from export
                                    </Button>
                                  )}
                                  {ticked.length > 0 && (
                                    <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={() => setDeleteTarget({ issue, records: ticked })}>
                                      Delete {ticked.length} record{ticked.length === 1 ? '' : 's'}
                                    </Button>
                                  )}
                                  {toRestore.length > 0 && (
                                    <Button size="small" onClick={() => handleRestoreRecords(issue, toRestore)}>
                                      Restore {toRestore.length} record{toRestore.length === 1 ? '' : 's'}
                                    </Button>
                                  )}
                                  {toInclude.length > 0 && (
                                    <Button size="small" onClick={() => handleIncludeRecords(toInclude)}>
                                      Include {toInclude.length} in export
                                    </Button>
                                  )}
                                  {ticked.length > 0 && <Button size="small" onClick={() => selectRecords(issue, [])}>Clear</Button>}
                                </Box>
                              );
                            })()}
                            <Table size="small" sx={{ mb: 1, '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      inputProps={{ 'aria-label': 'Select all records' }}
                                      checked={issue.records.length > 0 && (selected[key]?.size ?? 0) === issue.records.length}
                                      indeterminate={(selected[key]?.size ?? 0) > 0 && (selected[key]?.size ?? 0) < issue.records.length}
                                      onChange={(e) => selectRecords(issue, e.target.checked ? issue.records.map((r) => r.id) : [])}
                                    />
                                  </TableCell>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Farm</TableCell>
                                  <TableCell>Field</TableCell>
                                  <TableCell>Operation</TableCell>
                                  <TableCell>Products</TableCell>
                                  <TableCell>Gene Stack</TableCell>
                                  {issue.records.some((r) => r.note) && <TableCell>Found</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {issue.records.map((r, index) => (
                                  <TableRow
                                    key={r.id}
                                    hover
                                    selected={!!selected[key]?.has(r.id)}
                                    title="Double-click to edit"
                                    sx={{ cursor: 'pointer', ...(r.dismissal || r.excludedFromExport ? { opacity: 0.55 } : {}) }}
                                    onDoubleClick={() => setEditTarget({ recordId: r.id, businessId: issue.businessId })}
                                  >
                                    <TableCell padding="checkbox" onDoubleClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        size="small"
                                        inputProps={{ 'aria-label': 'Select record' }}
                                        checked={!!selected[key]?.has(r.id)}
                                        // React's checkbox onChange is a click, so it carries shiftKey.
                                        onChange={(e) => tickRecord(issue, index, (e.nativeEvent as MouseEvent).shiftKey)}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      {new Date(r.date).toLocaleDateString('en-AU')}
                                      {r.dismissal && (
                                        <Tooltip title={`Dismissed${r.dismissal.dismissedBy ? ` by ${r.dismissal.dismissedBy}` : ''} on ${new Date(r.dismissal.dateCreated).toLocaleDateString('en-AU')}${r.dismissal.note ? ` — ${r.dismissal.note}` : ''}`}>
                                          <Typography variant="caption" color="text.secondary" display="block">Dismissed</Typography>
                                        </Tooltip>
                                      )}
                                      {r.excludedFromExport && (
                                        <Tooltip title={r.exportExclusionNote ?? ''}>
                                          <Typography variant="caption" color="text.secondary" display="block">Excluded from export</Typography>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.farmName ?? '—'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      {r.fieldName ?? (r.unmatchedFieldText ? (
                                        <Box component="span" sx={{ color: 'warning.main' }}>{r.unmatchedFieldText} (not matched)</Box>
                                      ) : '—')}
                                    </TableCell>
                                    <TableCell>{r.operationTypeName}</TableCell>
                                    <TableCell>{r.products}</TableCell>
                                    <TableCell>{r.geneStackName ?? '—'}</TableCell>
                                    {issue.records.some((rec) => rec.note) && <TableCell>{r.note ?? '—'}</TableCell>}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RecordEditDialog
        open={!!editTarget}
        businessId={editTarget?.businessId ?? null}
        recordId={editTarget?.recordId ?? null}
        onClose={() => setEditTarget(null)}
        onSaved={fetchIssues}
      />

      <ExportExclusionDialog
        recordIds={excludeTarget}
        authHeaders={authHeaders}
        onClose={() => setExcludeTarget(null)}
        onDone={fetchIssues}
        onError={setError}
      />

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete records</DialogTitle>
        <DialogContent>
          {deleteTarget && (() => {
            const imported = deleteTarget.records.filter((r) => r.fromImport).length;
            const byHand = deleteTarget.records.length - imported;
            return (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{deleteTarget.issue.message}</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Permanently delete <strong>{deleteTarget.records.length}</strong> record(s)
                  {deleteTarget.records.length < deleteTarget.issue.records.length ? ` of this issue's ${deleteTarget.issue.records.length}` : ''}.{' '}
                  {imported > 0 && <>{imported} came from an import: their rows go back to unmatched in that import's review, ready to approve again. </>}
                </Typography>
                {byHand > 0 && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {byHand} record(s) weren't imported — they're gone for good once deleted.
                  </Alert>
                )}
                {deleteTarget.issue.ruleKey === 'field-differs-from-import' && (
                  <Alert severity="info">
                    Fix the farm/field alias first (Record Management → Farm Map / Field Map), or the rows will match the
                    same way again.
                  </Alert>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteRecords} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!updateTarget} onClose={() => !updating && setUpdateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Update {updateTarget?.records.length} record{updateTarget?.records.length === 1 ? '' : 's'}
        </DialogTitle>
        <DialogContent>
          {updateTarget && (() => {
            const first = updateTarget.records.reduce((a, r) => (r.date < a ? r.date : a), updateTarget.records[0].date);
            const last = updateTarget.records.reduce((a, r) => (r.date > a ? r.date : a), updateTarget.records[0].date);
            const operations = Array.from(new Set(updateTarget.records.map((r) => r.operationTypeName)));
            const geneStackNames = Array.from(new Set(updateTarget.records.map((r) => r.geneStackName ?? 'no gene stack')));
            return (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Now dated {new Date(first).toLocaleDateString('en-AU')}
                {first !== last ? ` to ${new Date(last).toLocaleDateString('en-AU')}` : ''}; {operations.join(', ')}; {geneStackNames.join(', ')}.
                {' '}Set what should change; blank fields are left as they are.
              </Typography>
            );
          })()}
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              type="date"
              label="Date"
              value={updateDate}
              onChange={(e) => setUpdateDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Each record keeps its time of day"
            />
            <FormControl fullWidth>
              <InputLabel id="bulk-update-operation">Operation</InputLabel>
              <Select
                labelId="bulk-update-operation"
                label="Operation"
                value={updateOperationTypeId}
                onChange={(e) => setUpdateOperationTypeId(e.target.value)}
              >
                <MenuItem value=""><em>Leave as is</em></MenuItem>
                {operationTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Autocomplete
              options={regions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={updateRegion}
              onChange={(_, value) => setUpdateRegion(value)}
              renderInput={(params) => <TextField {...params} label="Region" placeholder="Leave as is" />}
            />
            <Autocomplete
              options={geneStacks}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={updateGeneStack}
              onChange={(_, value) => setUpdateGeneStack(value)}
              renderInput={(params) => <TextField {...params} label="Gene stack" placeholder="Leave as is" />}
            />
            <TextField
              type="number"
              label="Band %"
              placeholder="Leave as is"
              InputLabelProps={{ shrink: true }}
              value={updateBand}
              onChange={(e) => setUpdateBand(e.target.value)}
              error={bandInputInvalid(updateBand)}
              helperText={bandInputInvalid(updateBand) ? 'Must be above 0 and at most 100' : 'Application rates stay as they are; 100 is the whole area'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateTarget(null)} disabled={updating}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateRecords}
            disabled={updating || bandInputInvalid(updateBand) || (!updateDate && !updateOperationTypeId && !updateRegion && !updateGeneStack && !updateBand.trim())}
          >
            {updating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!dismissTarget} onClose={() => setDismissTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dismissTarget?.recordIds
            ? `Dismiss ${dismissTarget.recordIds.length} record${dismissTarget.recordIds.length === 1 ? '' : 's'}`
            : 'Dismiss issue'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{dismissTarget?.issue.message}</Typography>
          {dismissTarget?.recordIds && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Only the ticked records stop being listed under "{ruleName(dismissTarget.issue.ruleKey)}". The rest of this
              issue stays, and the records are still checked by the other rules.
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note (optional)"
            helperText="Why it's fine — shown when dismissed issues are listed."
            value={dismissNote}
            onChange={(e) => setDismissNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissTarget(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleDismiss} disabled={saving}>{saving ? 'Saving...' : 'Dismiss'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataValidationManagement;
